// @refresh reset
import React, { createContext, useState, useEffect, useContext, useCallback, ReactNode } from 'react';
import { useGoogleOneTapLogin } from '@react-oauth/google';

declare global {
  interface Window {
    google?: { accounts?: { id?: { revoke?: (email: string, cb: () => void) => void } } };
  }
}
import { useNavigate, useLocation } from 'react-router-dom';
import { clearSyncState } from '../services/queueService';
import { AuthUser } from '../types/auth';
import { fetchMeAsync } from '../services/api/auth';
import {
  setIdToken,
  wasAuthenticated,
  getCachedProfile,
  persistAuthState,
  clearAuthState,
  getEmailFromToken,
} from '../services/googleAuthSession';

import type { AuthContextType } from '../interfaces/auth-context';

const AuthContext = createContext<AuthContextType>({
  user: null,
  idToken: null,
  isAuthenticated: false,
  isLoading: true,
  authError: null,
  loginWithGoogleJwt: async () => {},
  triggerSilentLogin: () => {},
  logout: async () => {},
});

// Eigene Komponente, damit useGoogleOneTapLogin bei jedem triggerSilentLogin()-Aufruf
// (per `key`-Wechsel am Aufrufer) neu gemountet wird und einen frischen Prompt-Zyklus startet -
// der Hook selbst bietet keine imperative "jetzt erneut versuchen"-Funktion.
const GoogleOneTapListener: React.FC<{ onSuccess: (token: string) => void; disabled: boolean }> = ({
  onSuccess,
  disabled,
}) => {
  useGoogleOneTapLogin({
    onSuccess: (r) => r.credential && onSuccess(r.credential),
    disabled,
    auto_select: true,
    use_fedcm_for_prompt: false,
  });
  return null;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<'wrong-account' | 'logged-out' | null>(null);
  const [silentLoginTrigger, setSilentLoginTrigger] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const triggerSilentLogin = () => setSilentLoginTrigger(n => n + 1);

  const loginWithGoogleJwt = useCallback(async (token: string): Promise<void> => {
    try {
      const profile = await fetchMeAsync(token);
      setIdTokenState(token);
      setIdToken(token);
      setUser(profile);
      setAuthError(null);
      setIsLoading(false);
      persistAuthState(profile);
      if (location.pathname === '/login' || location.pathname === '/') {
        navigate('/projects');
      }
    } catch (err: any) {
      console.error('[Auth] loginWithGoogleJwt fehlgeschlagen:', err);
      if (err?.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:wrong-account'));
      }
    }
  }, [navigate]);

  useEffect(() => {
    // Gecachtes Profil sofort laden → Seite wird direkt angezeigt
    // One Tap läuft im Hintergrund und erneuert den Token still
    if (wasAuthenticated()) {
      const cached = getCachedProfile();
      if (cached) setUser(cached);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleWrongAccount = () => {
      const email = getEmailFromToken(idToken);
      if (email) window.google?.accounts?.id?.revoke?.(email, () => {});
      setAuthError('wrong-account');
      setIdTokenState(null);
      setIdToken(null);
    };
    const handleUnauthorized = () => {
      clearAuthState();
      setUser(null);
      setIdTokenState(null);
      setIdToken(null);
    };
    const handleOnline = () => triggerSilentLogin();

    window.addEventListener('auth:wrong-account', handleWrongAccount);
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('auth:wrong-account', handleWrongAccount);
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('online', handleOnline);
    };
  }, [idToken]);

  const logout = (): void => {
    setIdTokenState(null);
    setIdToken(null);
    setUser(null);
    setAuthError('logged-out');
    clearAuthState();
    clearSyncState();
    navigate('/login');
  };

  const contextValue: AuthContextType = {
    user,
    idToken,
    isAuthenticated: !!user,
    isLoading,
    authError,
    loginWithGoogleJwt,
    triggerSilentLogin,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {/* key erzwingt Remount bei triggerSilentLogin() (z.B. nach 'online') für einen frischen Prompt-Zyklus.
          disabled hängt bewusst NICHT von idToken ab, damit der Token auch während einer aktiven Session
          still im Hintergrund erneuert wird. */}
      <GoogleOneTapListener
        key={silentLoginTrigger}
        onSuccess={loginWithGoogleJwt}
        disabled={!!authError}
      />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
